import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { loadRemoteCover } from '@/lib/remoteCoverQueue'

import { Modal } from './Modal'
import styles from './mediaGallery.module.css'

const failedRemoteCovers = new Set<string>()
const RETRY_REMOTE_COVERS = 'dtk:retry-remote-covers'

export function clearRemoteCoverFailures(): void {
  failedRemoteCovers.clear()
}

/** Explicit user action; never automatically retries failed upstream links. */
export function retryRemoteCovers(): void {
  clearRemoteCoverFailures()
  window.dispatchEvent(new Event(RETRY_REMOTE_COVERS))
}

export function MediaThumbnail({ cover, video, image, remote, remoteSession }: {
  cover: string | null; video: string | null; image?: string | null; remote?: string | null; remoteSession?: string | null
}) {
  const { t } = useTranslation('console')
  const [coverFailed, setCoverFailed] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)
  const [remoteFailed, setRemoteFailed] = useState(() => !!remote && failedRemoteCovers.has(remote))
  const [remoteBlob, setRemoteBlob] = useState<string | null>(null)
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined')
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      const inRange = entries.some((entry) => entry.isIntersecting)
      setVisible(inRange)
      if (!inRange) { setVideoReady(false); setRemoteBlob(null) }
    }, { rootMargin: '200px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (!remoteFailed) return
    const retry = () => { setRemoteBlob(null); setRemoteFailed(false) }
    window.addEventListener(RETRY_REMOTE_COVERS, retry)
    return () => window.removeEventListener(RETRY_REMOTE_COVERS, retry)
  }, [remoteFailed])
  useEffect(() => {
    if (!visible || (cover && !coverFailed) || !video || videoFailed || videoReady) return
    // A stalled decoder may never dispatch an error. Do not keep a blank tile
    // forever when an image or user-approved remote cover can be shown instead.
    const timer = window.setTimeout(() => setVideoFailed(true), 8000)
    return () => window.clearTimeout(timer)
  }, [visible, cover, coverFailed, video, videoFailed, videoReady])
  useEffect(() => {
    if (!remote || !remoteSession || !visible || remoteFailed || (cover && !coverFailed) || (video && !videoFailed) || (image && !imageFailed)) return
    const controller = new AbortController()
    let blobUrl: string | null = null
    void loadRemoteCover(remote, remoteSession, controller.signal).then((blob) => {
      if (!controller.signal.aborted) {
        blobUrl = URL.createObjectURL(blob)
        setRemoteBlob(blobUrl)
      }
    }).catch(() => {
      if (!controller.signal.aborted) { failedRemoteCovers.add(remote); setRemoteFailed(true) }
    })
    return () => { controller.abort(); if (blobUrl) URL.revokeObjectURL(blobUrl) }
  }, [remote, remoteSession, visible, remoteFailed, cover, coverFailed, video, videoFailed, image, imageFailed])
  return <span ref={ref} className={styles.thumbnailFrame}>
    {cover && !coverFailed ? <img src={cover} alt="" loading="lazy" onError={() => setCoverFailed(true)} />
      : video && !videoFailed ? visible ? <video src={`${video}#t=0.1`} preload="metadata" muted playsInline aria-hidden="true" onLoadedData={() => setVideoReady(true)} onError={() => setVideoFailed(true)} /> : null
      : image && !imageFailed ? <img src={image} alt="" loading="lazy" onError={() => setImageFailed(true)} />
      : remote && remoteBlob && !remoteFailed && visible ? <img src={remoteBlob} alt="" loading="lazy" onError={() => { failedRemoteCovers.add(remote); setRemoteFailed(true) }} />
      : <span className={styles.thumbnailEmpty}>{t('library.card.noCover')}</span>}
  </span>
}

export interface GalleryImage {
  sources: string[]
  label: string
}

/** Try only the supplied sources; never send media URLs to a third-party proxy. */
export function GalleryImageView({ image, className, lazy = false }: {
  image: GalleryImage
  className?: string
  lazy?: boolean
}) {
  const { t } = useTranslation('console')
  const sourceKey = JSON.stringify(image.sources)
  const [state, setState] = useState({ sourceKey, attempt: 0 })
  const attempt = state.sourceKey === sourceKey ? state.attempt : 0
  const source = image.sources[attempt]
  return source ? (
    <img
      className={className ?? styles.thumbnail}
      src={source}
      alt={image.label}
      loading={lazy ? 'lazy' : undefined}
      referrerPolicy="no-referrer"
      onError={() => setState({ sourceKey, attempt: attempt + 1 })}
    />
  ) : <span className={styles.unavailable}>{t('library.preview.unavailable')}</span>
}

export function MediaGallery({ images, index, onIndexChange, onClose }: {
  images: GalleryImage[]
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
}) {
  const { t } = useTranslation('console')
  const current = index === null ? undefined : images[index]
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  useEffect(() => {
    if (index === null || images.length < 2) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        const direction = event.key === 'ArrowLeft' ? -1 : 1
        onIndexChange((index + direction + images.length) % images.length)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [index, images.length, onIndexChange])

  return (
    <Modal open={index !== null && !!current} onClose={onClose} title={current?.label ?? ''} size="xl" className={styles.modal}>
      {current && index !== null ? (
        <div
          className={styles.stage}
          onTouchStart={(event) => {
            const touch = event.touches[0]
            touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null
          }}
          onTouchEnd={(event) => {
            const start = touchStart.current
            touchStart.current = null
            const touch = event.changedTouches[0]
            if (!start || !touch || images.length < 2) return
            const dx = touch.clientX - start.x
            const dy = touch.clientY - start.y
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) {
              onIndexChange((index + (dx < 0 ? 1 : -1) + images.length) % images.length)
            }
          }}
        >
          {images.length > 1 ? <button type="button" className={`${styles.nav} ${styles.previous}`} aria-label={t('library.preview.previous')} onClick={() => onIndexChange((index - 1 + images.length) % images.length)}>‹</button> : null}
          <GalleryImageView key={`${index}:${current.sources.join('|')}`} image={current} className={styles.image} />
          {images.length > 1 ? <button type="button" className={`${styles.nav} ${styles.next}`} aria-label={t('library.preview.next')} onClick={() => onIndexChange((index + 1) % images.length)}>›</button> : null}
          <span className={styles.count} aria-live="polite">{index + 1} / {images.length}</span>
        </div>
      ) : null}
    </Modal>
  )
}
